import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Card, CardContent, Typography, Box,
  CircularProgress, Alert, Tabs, Tab
} from '@mui/material';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  getDegreeBreakdown, getGraduationTrends, getGenderDistribution,
  getGeographicalDistribution, getHonorsDistribution,
  getContactCompleteness, getSummaryStatistics
} from '../services/api';

const COLORS = ['#046A38', '#FCCA18', '#4A4A4A', '#D4E4DB', '#FF8042', '#00C49F', '#FFBB28'];

const StatisticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Data states
  const [summary, setSummary] = useState(null);
  const [degreeData, setDegreeData] = useState([]);
  const [graduationData, setGraduationData] = useState([]);
  const [genderData, setGenderData] = useState([]);
  const [geographyData, setGeographyData] = useState([]);
  const [honorsData, setHonorsData] = useState([]);
  const [contactData, setContactData] = useState([]);

  useEffect(() => {
    fetchAllStatistics();
  }, []);

  const fetchAllStatistics = async () => {
    setLoading(true);
    setError('');

    try {
      const [
        summaryRes,
        degreeRes,
        gradRes,
        genderRes,
        geoRes,
        honorsRes,
        contactRes
      ] = await Promise.all([
        getSummaryStatistics(),
        getDegreeBreakdown(),
        getGraduationTrends(),
        getGenderDistribution(),
        getGeographicalDistribution(),
        getHonorsDistribution(),
        getContactCompleteness()
      ]);

      setSummary(summaryRes);
      setDegreeData(degreeRes.data || []);
      setGraduationData(gradRes.data || []);
      setGenderData(genderRes.data || []);
      setGeographyData(geoRes.data || []);
      setHonorsData(honorsRes.data || []);
      setContactData(contactRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading statistics...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Alumni Statistics Dashboard
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Alumni"
            value={summary?.totalAlumni || 0}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Degrees"
            value={summary?.totalDegrees || 0}
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Countries"
            value={summary?.totalCountries || 0}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Latest Grad Year"
            value={summary?.latestGradYear || 'N/A'}
            color="info.main"
          />
        </Grid>
      </Grid>

      {/* Tabs for Charts */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Degree Breakdown" />
          <Tab label="Graduation Trends" />
          <Tab label="Gender Distribution" />
          <Tab label="Geography" />
          <Tab label="Honors" />
          <Tab label="Contact Info" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <ChartCard title="Degree Distribution">
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={degreeData}
                dataKey="count"
                nameKey="degree"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={(entry) => `${entry.degree}: ${entry.count}`}
              >
                {degreeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <ChartCard title="Graduation Trends Over Time">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={graduationData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#046A38" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <ChartCard title="Gender Distribution">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={genderData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="gender" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#046A38" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <ChartCard title="Geographical Distribution (Top Countries)">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={geographyData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="country" type="category" width={150} />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#FCCA18" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <ChartCard title="Honors Achieved">
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={honorsData}
                dataKey="count"
                nameKey="honor"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label
              >
                {honorsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <ChartCard title="Contact Information Completeness">
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={contactData}
                dataKey="count"
                nameKey="status"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label
              >
                {contactData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </TabPanel>
    </Container>
  );
};

// Helper Components
const SummaryCard = ({ title, value, color }) => (
  <Card>
    <CardContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ color }}>
        {value.toLocaleString()}
      </Typography>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, children }) => (
  <Card>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ mt: 2 }}>
        {children}
      </Box>
    </CardContent>
  </Card>
);

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box>{children}</Box>}
  </div>
);

export default StatisticsPage;